import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Loader2, Sparkles, Users } from "lucide-react";

export default function TeamJoin() {
  const { token } = useParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", country_code: "IN" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.getTeamInvite(token)
      .then((inv) => { setInvite(inv); setForm((f) => ({ ...f, name: inv.invitee_name || "" })); })
      .catch(() => toast.error("Invite not found"));
    api.countries().then(setCountries).catch(() => {});
  }, [token]);

  const begin = async () => {
    if (!form.name || !form.location) { toast.error("Please fill in your name and location"); return; }
    setLoading(true);
    try {
      const { user } = await api.startInvite(token, form);
      login(user);
      navigate(`/assessment?invite=${token}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  if (!invite) return <div className="max-w-lg mx-auto px-5 py-24 text-center text-[#918780]">Loading…</div>;

  if (invite.status === "completed") {
    return (
      <div className="max-w-lg mx-auto px-5 py-20 text-center fade-up">
        <h1 className="font-display text-3xl font-bold text-[#2B2625] mb-3">You've already joined 🎉</h1>
        <p className="text-[#6E655F] mb-6">Thanks for completing your assessment for {invite.team_name}.</p>
        <Link to="/dashboard" className="inline-flex items-center gap-2 bg-[#6B21A8] text-white font-semibold py-3 px-7 rounded-xl">
          Go to dashboard <ArrowRight size={18} />
        </Link>
      </div>
    );
  }

  const field = "w-full px-4 py-3 rounded-xl border border-[#E8E1D9] bg-white focus:outline-none focus:border-[#6B21A8] focus:ring-2 focus:ring-[#6B21A8]/15 transition";

  return (
    <div className="max-w-xl mx-auto px-5 py-14 fade-up">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl bg-[#3F3A44] text-white p-8 sm:p-10 mb-8 text-center">
        <span className="w-14 h-14 rounded-2xl bg-white/15 grid place-items-center mx-auto mb-4"><Users size={26} /></span>
        <h1 className="font-display text-2xl sm:text-3xl font-bold leading-snug">
          {invite.inviter_name} invited you to join {invite.team_name}
        </h1>
        <p className="text-[#E4D6F5] mt-3">
          You've been added as {invite.role}. This short, science-based assessment helps your whole team
          understand how you each work — and how to collaborate even better together.
        </p>
        <p className="text-white/90 font-semibold mt-4 flex items-center justify-center gap-2">
          <Sparkles size={16} /> Free for you · about 12 minutes
        </p>
      </motion.div>

      <div className="bg-white border border-[#E8E1D9] rounded-2xl p-7 shadow-sm space-y-4">
        <p className="text-[#6E655F] text-sm">Joining as <b className="text-[#2B2625]">{invite.invitee_email}</b></p>
        <div>
          <label className="text-sm font-semibold text-[#2B2625] block mb-1.5">Your name</label>
          <input data-testid="teamjoin-name" className={field} value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
        </div>
        <div>
          <label className="text-sm font-semibold text-[#2B2625] block mb-1.5">City / Location</label>
          <input data-testid="teamjoin-location" className={field} value={form.location}
                 onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Mumbai" />
        </div>
        <div>
          <label className="text-sm font-semibold text-[#2B2625] block mb-1.5">Country</label>
          <select data-testid="teamjoin-country" className={field} value={form.country_code}
                  onChange={(e) => setForm({ ...form, country_code: e.target.value })}>
            {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
        <button data-testid="teamjoin-begin" onClick={begin} disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#6B21A8] hover:bg-[#4C1D95] text-white font-semibold py-3.5 rounded-xl transition-colors active:scale-[0.98] disabled:opacity-60">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <>Take the assessment <ArrowRight size={18} /></>}
        </button>
      </div>
    </div>
  );
}
